import type { BroadcastContent } from '../../../api/types';
import { useState } from 'react';
import { ssmlToPlainText } from '../../../utils/karaoke';
import { KaraokeText } from '../../KaraokeText';
import { findPollyLanguage } from '../../../data/pollyVoices';
import { usePreviewVoiceSelections } from '../PreviewVoiceContext';
import { buildPreviewQueue } from '../previewLanguages';
import { MultilingualPreviewButton, MultilingualPreviewStatus } from './MultilingualPreviewButton';
import { useMultilingualPreview } from '../hooks/useMultilingualPreview';
import { PREVIEW_AUDIO_TESTID_PREFIX, PREVIEW_BUTTON_TESTID_PREFIX } from '../constants';
import { useAudioPreview } from '../hooks/useAudioPreview';
import { usePreviewSample } from '../hooks/usePreviewSample';
import {
  buildSampleContext,
  deriveContentSampleLimits,
  resolveSampleFields,
} from '../previewSample';
import type { PlaceholderItem, PreviewSampleContext, PreviewSsmlHandler } from '../types';
import { PreviewSamplePanel } from './PreviewSamplePanel';

/** `onPreviewSsml` 未指定時のフック用の代替。UI 自体を出さないので呼ばれない。 */
const unavailablePreview: PreviewSsmlHandler = () =>
  Promise.reject(new Error('プレビュー処理が指定されていません'));

/**
 * 放送コンテンツの音声プレビュー UI (共通部品) — Issue #439。
 *
 * ## 何を解決するか
 *
 * 従来プレビュー UI は `FreeTextFields.tsx` の中に埋め込まれており、
 * 「任意テキスト / SSML をそのまま Polly に投げる」以外のことができなかった。
 * 本部品は **どのフィールドからでも呼べる形** に切り出したもので、
 * 自列車遅延 / 接続線遅延 / 接続線遅延(乗換駅別) / 音声ファイルのすべてで
 * 同じ操作感のプレビューを提供する。
 *
 * ## サーバ側レンダリングを前提とする
 *
 * `content` を渡すとサーバ (`POST /audio-preview`) が UpdateContents と同じ
 * `ssml_builder` で文面を組み立てるため、**プレビューで聴いた文面 = 本番の文面**
 * が保証される。フロントで文字置換していた場合に生じる
 * 「`{from_prefix}` の助詞付与」「`floor_delay_minutes()` の丸め」
 * 「`select_delay_pattern()` のパターン選択」のズレが構造的に起こらない。
 *
 * ## 表示要素
 *
 * 1. 「▶ 音声プレビュー」ボタン (生成中は disabled)
 * 2. 「▼ プレビュー条件」(サンプル値の詳細設定。凡例に載っている項目のみ表示)
 * 3. 生成後の `<audio controls>` (`playback_rate` を反映)
 * 4. サーバがレンダリングした文面 (`rendered_ssml`) の併記
 * 5. 未展開プレースホルダの警告 (`role="alert"`)
 */
export function AudioPreviewField({
  content,
  parentVoiceId,
  playbackRate,
  onPreviewSsml,
  testIdSuffix,
  label = '▶ 音声プレビュー',
  placeholders = [],
  sampleOverrides,
  disabled = false,
  disabledReason,
  legacyAudioTestId,
  onMatchedPatternIndexChange,
}: {
  /**
   * プレビュー対象の放送コンテンツ (カテゴリ + properties)。
   * サーバ側でこの内容をレンダリングしてから合成する。
   */
  content: BroadcastContent;
  /**
   * 親フォームで選択中の Polly VoiceId。実放送と同じ声色で試聴するために渡す。
   * 空文字 / 未指定なら送らず、サーバ側デフォルト (Takumi) にフォールバックする。
   */
  parentVoiceId?: string;
  /**
   * 親コンテンツの `playback_rate`。`<audio>` の `playbackRate` に反映する。
   * 範囲 (0.5〜2.0) 外 / 未指定は 1.0 (等倍)。
   */
  playbackRate?: number;
  /** プレビュー処理。未指定ならプレビュー UI 自体を表示しない (既存仕様を踏襲) */
  onPreviewSsml?: PreviewSsmlHandler;
  /**
   * `data-testid` のサフィックス (例: `'self-train-delay-0'` →
   * `data-testid="preview-button-self-train-delay-0"`)。
   * コンテンツ index とフィールド名から一意に決まる値を渡すこと。
   */
  testIdSuffix: string;
  /** ボタンのラベル (既定: 「▶ 音声プレビュー」) */
  label?: string;
  /**
   * このフィールドで使えるプレースホルダの凡例定義。
   * 「プレビュー条件」パネルに出す入力欄をここから決めるため、
   * `placeholders.ts` の欄別プレースホルダ定義を渡す
   * (whole_line モードのように乗換駅・方面を使わない画面では入力欄が出ない)。
   */
  placeholders?: PlaceholderItem[];
  /**
   * サンプル条件の初期値の上書き。編集中の行の設定値
   * (`delay_minutes_from` から導出した遅延秒数など) を反映するために使う。
   */
  sampleOverrides?: Partial<PreviewSampleContext>;
  /** プレビュー不可 (必須項目が空など) のときに true。ボタンを disabled にする */
  disabled?: boolean;
  /** `disabled` の理由 (ボタンの title / ヒントに表示する) */
  disabledReason?: string;
  /**
   * 後方互換用の `<audio>` の `data-testid`。
   * 任意テキスト放送の既存 E2E が `ssml-preview-audio` を参照しているため、
   * 規則的な `preview-audio-<suffix>` と **両方** を出せるようにする。
   */
  legacyAudioTestId?: string;
  /**
   * サーバが返した「採用された遅延パターン行の index」を親へ通知する。
   * パターン表の該当行をハイライトして「境界値でどの行が鳴るか」を可視化する用途。
   */
  onMatchedPatternIndexChange?: (index: number | null) => void;
}): React.JSX.Element | null {
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
  const [multilingualPlaying, setMultilingualPlaying] = useState(false);
  // Issue #643: 再生中の文面をカラオケ風に表示するための状態 (<audio> のイベントで更新)。
  const [karaokePlaying, setKaraokePlaying] = useState(false);
  const [karaokeProgress, setKaraokeProgress] = useState(0);
  const voiceSelections = usePreviewVoiceSelections();
  const previewQueue = buildPreviewQueue([content], parentVoiceId, voiceSelections);
  const selectedPlan =
    previewQueue.find((plan) => plan.language === selectedLanguage) ?? previewQueue[0];
  const {
    previewing,
    previewError,
    previewUrl,
    previewAudioRef,
    effectivePlaybackRate,
    startPreview,
    lastResult,
  } = useAudioPreview({ onPreviewSsml, playbackRate });

  /*
   * サンプル条件パネルの開閉と現在値 (共通フック `usePreviewSample` に委譲)。
   *
   * 既定値は凡例定義の `sample` + 呼び出し側の overrides から組み立てるため、
   * ユーザーは追加入力なしで 1 クリック試聴できる。
   * かつ「未編集のキーだけ `sampleOverrides` を適用する」規則により、
   * 行の設定変更には追従しつつ手入力は上書きしない
   * (詳細は `usePreviewSample` のコメントを参照)。
   */
  /*
   * 指定できる入力枠 (Issue #445 / レビュー反映)。
   *
   * 「現在設定可能な項目に応じて設定できるように」という要望に応え、
   * 条件パネルの接続線・方面の入力欄は **編集中コンテンツの設定内容** から決める。
   * 設定に無い接続線を入力できてもサーバ側では読み上げられないため、
   * 入力枠を設定に合わせることで「入力したのに鳴らない」食い違いを構造的に防ぐ。
   */
  const sampleLimits = deriveContentSampleLimits(content);

  const sampleState = usePreviewSample({
    initial: () => buildSampleContext(placeholders, sampleOverrides),
    overrides: sampleOverrides,
    limits: sampleLimits,
  });

  /*
   * 多言語の通し試聴。状態はここで持ち、ボタンは操作ボタンの行に、
   * 再生中の文面とエラーは行の外 (通常の試聴の `<audio>` の下と同じ並び) に置く。
   * 行の中に出すと、文面の幅でボタンが次の段へ押し出される。
   * `onPreviewSsml` が無いと UI 自体を出さないため、フックは早期 return より前に呼ぶ。
   */
  const multilingual = useMultilingualPreview({
    content,
    parentVoiceId,
    playbackRate,
    onPreviewSsml: onPreviewSsml ?? unavailablePreview,
    resolveSample: sampleState.resolve,
    onPlayingChange: (playing) => {
      if (playing) previewAudioRef.current?.pause();
      setMultilingualPlaying(playing);
    },
  });

  // 既存仕様どおり、ハンドラ未指定ならプレビュー UI は出さない。
  if (!onPreviewSsml) return null;

  const fields = resolveSampleFields(placeholders);
  const hasSampleFields =
    fields.showDelay ||
    fields.showRouteName ||
    fields.showFromStation ||
    fields.showTransferStation ||
    fields.showDirectionName ||
    fields.showDepartureTime ||
    fields.showHeadsign;

  const handlePreview = async (): Promise<void> => {
    if (!selectedPlan) return;
    const { language, voiceId } = selectedPlan;
    // 未編集のキーだけ `sampleOverrides` (編集中の行の値) を適用する。
    // 「行の遅れ分数を変えてから試聴した」場合は追従し、
    // 「条件パネルで手入力した」場合はその値を尊重する。
    const effectiveSample = sampleState.resolve();
    const result = await startPreview(
      voiceId
        ? { content, sampleContext: effectiveSample, voiceId, language }
        : { content, sampleContext: effectiveSample, language },
    );
    if (result && onMatchedPatternIndexChange) {
      onMatchedPatternIndexChange(result.matchedPatternIndex ?? null);
    }
  };

  const unresolvedTokens = lastResult?.unresolvedTokens ?? [];
  const karaokeText = ssmlToPlainText(lastResult?.renderedSsml);
  // 「読み上げた文面」は辞書置換後 (実際に Polly へ渡した SSML) を優先して表示する。
  // 辞書が効かなかった場合は applied が無いので、従来どおり rendered を表示する。
  const spokenSsml = lastResult?.appliedSsml || lastResult?.renderedSsml;

  return (
    <div className="ssml-preview-field" data-testid={`preview-field-${testIdSuffix}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {previewQueue.length > 1 ? (
          <select
            aria-label={`試聴言語 (${testIdSuffix})`}
            value={selectedPlan?.language}
            disabled={previewing || multilingualPlaying}
            onChange={(event) => setSelectedLanguage(event.target.value)}
          >
            {previewQueue.map((plan) => (
              <option key={plan.language} value={plan.language}>
                {findPollyLanguage(plan.language)?.label}
              </option>
            ))}
          </select>
        ) : null}
        <button
          type="button"
          disabled={previewing || disabled || multilingualPlaying || !selectedPlan}
          aria-label={`音声プレビュー (${testIdSuffix})`}
          title={disabled ? disabledReason : undefined}
          data-testid={`${PREVIEW_BUTTON_TESTID_PREFIX}-${testIdSuffix}`}
          onClick={() => void handlePreview()}
        >
          {previewing ? '生成中...' : label}
        </button>
        {previewQueue.length > 1 ? (
          <MultilingualPreviewButton
            state={multilingual}
            testIdSuffix={testIdSuffix}
            disabled={disabled || previewing}
          />
        ) : null}
        {hasSampleFields ? (
          <button
            type="button"
            aria-expanded={sampleState.open}
            aria-label={`プレビュー条件 (${testIdSuffix})`}
            data-testid={`preview-sample-toggle-${testIdSuffix}`}
            onClick={sampleState.toggle}
          >
            {sampleState.open ? '▼' : '▶'} プレビュー条件
          </button>
        ) : null}
        {disabled && disabledReason ? (
          <span className="hint" data-testid={`preview-disabled-hint-${testIdSuffix}`}>
            {disabledReason}
          </span>
        ) : null}
      </div>

      {/*
        プレビュー条件 (サンプル値) パネル。
        「1分/5分/10分単位の丸めが意図どおりか」「境界値 (4分/5分) でどのパターンが鳴るか」を
        保存もデプロイもせずに確認できるのが大きな価値。
      */}
      {sampleState.open && hasSampleFields ? (
        <PreviewSamplePanel
          testIdSuffix={testIdSuffix}
          fields={fields}
          sample={sampleState.shown}
          onChange={sampleState.update}
          // Issue #445: 接続線・方面は「設定されている件数ぶん」指定できる。
          limits={sampleLimits}
          onLineChange={sampleState.updateLine}
          onDirectionChange={sampleState.updateDirection}
        />
      ) : null}

      {previewUrl ? (
        <audio
          ref={previewAudioRef}
          controls
          src={previewUrl}
          data-testid={legacyAudioTestId ?? `${PREVIEW_AUDIO_TESTID_PREFIX}-${testIdSuffix}`}
          style={{ display: 'block', marginTop: 4 }}
          onPlay={() => {
            setKaraokeProgress(0);
            setKaraokePlaying(true);
          }}
          onPause={() => setKaraokePlaying(false)}
          onEnded={() => {
            setKaraokeProgress(1);
            setKaraokePlaying(false);
          }}
          onTimeUpdate={(e) => {
            const { currentTime, duration } = e.currentTarget;
            if (Number.isFinite(duration) && duration > 0) {
              setKaraokeProgress(currentTime / duration);
            }
          }}
          onLoadedMetadata={(e) => {
            // src 変更・再生開始のタイミングで playbackRate がリセットされることがあるため、
            // useAudioPreview の useEffect と本ハンドラの両方で反映する。
            (e.currentTarget as HTMLAudioElement).playbackRate = effectivePlaybackRate;
          }}
        >
          <track kind="captions" />
        </audio>
      ) : null}

      {/*
        規則的な testid (`preview-audio-<suffix>`) と後方互換 testid を両立させるため、
        legacy を使う場合は同じ URL を指す隠し要素を追加で置く
        (E2E が `ssml-preview-audio` を、新規テストが `preview-audio-*` を参照できる)。
      */}
      {previewUrl && legacyAudioTestId ? (
        <span
          data-testid={`${PREVIEW_AUDIO_TESTID_PREFIX}-${testIdSuffix}`}
          data-preview-url={previewUrl}
          hidden
        />
      ) : null}

      {/*
        Issue #643: 再生中はカラオケのように読み上げ文面を追従表示する。
        文面はサーバがレンダリングした SSML からタグを除いたもの。
        文面が無い (音声ファイルモードなど) 場合は表示しない。
      */}
      {previewUrl && karaokePlaying && karaokeText ? (
        <KaraokeText
          text={karaokeText}
          progress={karaokeProgress}
          testId={`preview-karaoke-${testIdSuffix}`}
        />
      ) : null}

      {/* 多言語の通し試聴の文面とエラー (通常の試聴の文面と同じ、ボタン行の外) */}
      {previewQueue.length > 1 ? (
        <MultilingualPreviewStatus state={multilingual} testIdSuffix={testIdSuffix} />
      ) : null}

      {/* サーバがレンダリングした文面。「なぜこう鳴ったか」が分かり自己解決が進む */}
      {spokenSsml ? (
        <details
          className="hint"
          data-testid={`preview-rendered-ssml-${testIdSuffix}`}
          style={{ marginTop: 4 }}
        >
          <summary>読み上げた文面 (SSML)</summary>
          <code style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{spokenSsml}</code>
        </details>
      ) : null}

      {/* 未展開プレースホルダの警告 (誤ったトークン名の放送を防ぐ) */}
      {unresolvedTokens.length > 0 ? (
        <div
          className="error-message"
          role="alert"
          data-testid={`preview-unresolved-tokens-${testIdSuffix}`}
        >
          未対応のプレースホルダがあります: {unresolvedTokens.join(', ')}
        </div>
      ) : null}

      {previewError ? (
        <div className="error-message" role="alert">
          {previewError}
        </div>
      ) : null}
    </div>
  );
}
